import React from 'react'

const Tuple = () => {
    return (
        <div className='container'>
            <div class="editor-contents">
                <h1>Python Tuple</h1>

                
                <p class="containers">In this article, you'll learn everything about Python tuples. More specifically, what are tuples, how to create them, when to use them and various methods you should be familiar with.</p>
           </div>
            <div id="node-50" class="node node-python clearfix" about="/python-programming/tuple" typeof="sioc:Item foaf:Document">
                <span property="dc:title" content="Python Tuple" class="rdf-meta element-hidden"></span>

                <div class="content">
                    <p id="introduction">A tuple in Python is similar to a list. The difference between the two is that we cannot change the elements of a tuple once it is assigned whereas we can change the elements of a list.</p>

                    <hr />
                    <h2 id="create">Creating a Tuple</h2>

                    <p>A tuple is created by placing all the items (elements) inside parentheses <code>()</code>, separated by commas. The parentheses are optional, however, it is a good practice to use them.</p>

                    <p>A tuple can have any number of items and they may be of different types (integer, float, list, <a href="/python-programming/string">string</a>, etc.).</p>
<div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Different types of tuples</span>

                        <span class="comment"># Empty tuple</span> <br />
my_tuple = ()<br />
<span class="keywords">print</span>(my_tuple)<br />
<br />
<span class="comment"># Tuple having integers</span><br />
my_tuple = (<span class="hljs-number">1</span>, <span class="hljs-number">2</span>, <span class="hljs-number">3</span>)<br />
<span class="keywords">print</span>(my_tuple)<br />
<br />
<span class="comment"># tuple with mixed datatypes</span><br />
my_tuple = (<span class="hljs-number">1</span>, <span class="hljs-string">"Hello"</span>, <span class="hljs-number">3.4</span>)<br />
<span class="keywords">print</span>(my_tuple)<br />
<br />
<span class="comment"># nested tuple</span><br />
my_tuple = (<span class="hljs-string">"mouse"</span>, [<span class="hljs-number">8</span>, <span class="hljs-number">4</span>, <span class="hljs-number">6</span>], (<span class="hljs-number">1</span>, <span class="hljs-number">2</span>, <span class="hljs-number">3</span>))<br />
<span class="keywords">print</span>(my_tuple)<br /></code></pre></div>
<div className='output'>
                    <p><strong>Output</strong></p> 
                    <pre><samp>() <br />
                    (1, 2, 3)<br />
                    (1, 'Hello', 3.4)<br />
('mouse', [8, 4, 6], (1, 2, 3))<br /></samp></pre></div>

                    <p>A tuple can also be created without using parentheses. This is known as tuple packing.</p>
<br />
<div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs">my_tuple = <span class="hljs-number">3</span>, <span class="hljs-number">4.6</span>, <span class="hljs-string">"dog"</span> <br />
                        <span class="keywords">print</span>(my_tuple) <br />
                        <br />
<span class="comment"># tuple unpacking is also possible</span><br />
a, b, c = my_tuple<br />

<span class="keywords">print</span>(a)      <span class="comment"># 3</span><br />
                        <span class="keywords">print</span>(b)      <span class="comment"># 4.6</span><br />
                        <span class="keywords">print</span>(c)      <span class="comment"># dog</span></code></pre><br /></div>
                        <div className='output'> 
                    <p><strong>Output</strong></p>

                    <pre><samp>(3, 4.6, 'dog')<br />
                    3<br />
                    4.6<br />
dog</samp></pre></div>

                    <p>Creating a tuple with one element is a bit tricky.</p>

                    <p>Having one element within parentheses is not enough. We will need a trailing comma to indicate that it is, in fact, a tuple.</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs">my_tuple = (<span class="hljs-string">"hello"</span>)<br />
<span class="keywords">print</span>(type(my_tuple))  <span class="comment"># &lt;class 'str'&gt;</span><br />
<br />
                        <span class="comment"># Creating a tuple having one element</span><br />
my_tuple = (<span class="hljs-string">"hello"</span>,)<br />
<span class="keywords">print</span>(type(my_tuple))  <span class="comment"># &lt;class 'tuple'&gt;</span><br />
<br />
                        <span class="comment"># Parentheses is optional</span><br />
my_tuple = <span class="hljs-string">"hello"</span>,<br />
<span class="keywords">print</span>(type(my_tuple))  <span class="comment"># &lt;class 'tuple'&gt;</span></code></pre></div>
<div className='output'> 
                    <p><strong>Output</strong></p>
                    
                    <pre><samp>&lt;class 'str'&gt;<br />
                    &lt;class 'tuple'&gt;<br />
&lt;class 'tuple'&gt;</samp></pre></div>

                    <hr />
                    <h2 id="access">Access Tuple Elements</h2>

                    <p>There are various ways in which we can access the elements of a tuple.</p>

                    <h3 id="index">1. Indexing</h3>

                    <p>We can use the index operator <code>[]</code> to access an item in a tuple, where the index starts from 0.</p>

                    <p>So, a tuple having 6 elements will have indices from 0 to 5. Trying to access an index outside of the tuple index range(6,7,... in this example) will raise an <code>IndexError</code>.</p>

                    <p>The index must be an integer, so we cannot use float or other types. This will result in <code>TypeError</code>.</p>

                    <p>Likewise, nested tuples are accessed using nested indexing, as shown in the example below.</p>
<div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Accessing tuple elements using indexing</span><br />
my_tuple = (<span class="hljs-string">'p'</span>,<span class="hljs-string">'e'</span>,<span class="hljs-string">'r'</span>,<span class="hljs-string">'m'</span>,<span class="hljs-string">'i'</span>,<span class="hljs-string">'t'</span>)<br />
<br />
<span class="keywords">print</span>(my_tuple[<span class="hljs-number">0</span>])   <span class="comment"># 'p' </span><br />
                        <span class="keywords">print</span>(my_tuple[<span class="hljs-number">5</span>])   <span class="comment"># 't'</span><br />
                        <br />
                        <span class="comment"># IndexError: list index out of range</span><br />
                        <span class="comment"># print(my_tuple[6])</span><br />
                        <br />
                        <span class="comment"># Index must be an integer</span><br />
                        <span class="comment"># TypeError: list indices must be integers, not float</span><br />
                        <span class="comment"># my_tuple[2.0]</span><br />
                        <br />
                        <span class="comment"># nested tuple</span><br />
n_tuple = (<span class="hljs-string">"mouse"</span>, [<span class="hljs-number">8</span>, <span class="hljs-number">4</span>, <span class="hljs-number">6</span>], (<span class="hljs-number">1</span>, <span class="hljs-number">2</span>, <span class="hljs-number">3</span>))<br />
<br />
<span class="comment"># nested index</span><br />
                        <span class="keywords">print</span>(n_tuple[<span class="hljs-number">0</span>][<span class="hljs-number">3</span>])       <span class="comment"># 's'</span><br />
                        <span class="keywords">print</span>(n_tuple[<span class="hljs-number">1</span>][<span class="hljs-number">1</span>])       <span class="comment"># 4</span></code></pre></div>
<div className='output'>
                    <p><strong>Output</strong></p>

                    <pre><samp>p <br />
                    t<br />
                    s<br />
4</samp></pre></div>

                    <hr />
                    <h3 id="negative">2. Negative Indexing</h3>

                    <p>Python allows negative indexing for its sequences.</p>

                    <p>The index of -1 refers to the last item, -2 to the second last item and so on.</p>
<div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Negative indexing for accessing tuple elements</span><br />
my_tuple = (<span class="hljs-string">'p'</span>, <span class="hljs-string">'e'</span>, <span class="hljs-string">'r'</span>, <span class="hljs-string">'m'</span>, <span class="hljs-string">'i'</span>, <span class="hljs-string">'t'</span>)<br />
<br />
<span class="comment"># Output: 't'</span><br />
                        <span class="keywords">print</span>(my_tuple[<span class="hljs-number">-1</span>])<br />
                        <br />
<span class="comment"># Output: 'p'</span><br />
                        <span class="keywords">print</span>(my_tuple[<span class="hljs-number">-6</span>])</code></pre><br />

                    <p></p><div id="block-inject-1" class="block-inject block-inject-1">
</div>
                    </div>
                    <div className='output'>
                    <div class="clearfix"></div><p><strong>Output</strong></p>
                    <pre><samp>t<br />
p</samp></pre>
</div>
                    <hr />
                    <h3 id="slice">3. Slicing</h3>

                    <p>We can access a range of items in a tuple by using the slicing operator colon <code>:</code>.</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Accessing tuple elements using slicing</span><br />
my_tuple = (<span class="hljs-string">'p'</span>,<span class="hljs-string">'r'</span>,<span class="hljs-string">'o'</span>,<span class="hljs-string">'g'</span>,<span class="hljs-string">'r'</span>,<span class="hljs-string">'a'</span>,<span class="hljs-string">'m'</span>,<span class="hljs-string">'i'</span>,<span class="hljs-string">'z'</span>)<br />
<br />
<span class="comment"># elements 2nd to 4th</span><br />
                        <span class="comment"># Output: ('r', 'o', 'g')</span><br />
                        <span class="keywords">print</span>(my_tuple[<span class="hljs-number">1</span>:<span class="hljs-number">4</span>])<br />
                        <br />
<span class="comment"># elements beginning to 2nd</span><br />
                        <span class="comment"># Output: ('p', 'r')</span><br />
                        <span class="keywords">print</span>(my_tuple[:<span class="hljs-number">-7</span>])<br /><br />

<span class="comment"># elements 8th to end</span><br />
                        <span class="comment"># Output: ('i', 'z')</span><br />
                        <span class="keywords">print</span>(my_tuple[<span class="hljs-number">7</span>:])<br /><br />

<span class="comment"># elements beginning to end</span><br />
                        <span class="comment"># Output: ('p', 'r', 'o', 'g', 'r', 'a', 'm', 'i', 'z')</span><br />
                        <span class="keywords">print</span>(my_tuple[:])</code></pre>
</div>
<div className='output'>
                    <p><strong>Output</strong></p> 

                    <pre><samp>('r', 'o', 'g') <br />
                    ('p', 'r') <br />
                    ('i', 'z') <br />
('p', 'r', 'o', 'g', 'r', 'a', 'm', 'i', 'z')</samp></pre>
</div>
                    <p>Slicing can be best visualized by considering the index to be between the elements as shown below. So if we want to access a range, we need the index that will slice the portion from the tuple.</p>

                    <figure><img alt="Element Slicing in Python" height="126" src="//cdn.programiz.com/sites/tutorial2program/files/element-slicling.jpg" title="Element Slicing" width="400" />
                        <figcaption>Element Slicing in Python</figcaption>
                    </figure>

                    <hr />
                    <h2 id="change">Changing a Tuple</h2>

                    <p>Unlike lists, tuples are immutable.</p>

                    <p>This means that elements of a tuple cannot be changed once they have been assigned. But, if the element is itself a mutable data type like a list, its nested items can be changed.</p>

                    <p>We can also assign a tuple to different values (reassignment).</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Changing tuple values</span><br />
my_tuple = (<span class="hljs-number">4</span>, <span class="hljs-number">2</span>, <span class="hljs-number">3</span>, [<span class="hljs-number">6</span>, <span class="hljs-number">5</span>])<br />
<br /><br />

<span class="comment"># TypeError: 'tuple' object does not support item assignment</span><br />
                        <span class="comment"># my_tuple[1] = 9</span><br /><br />

                        <span class="comment"># However, item of mutable element can be changed</span><br />
my_tuple[<span class="hljs-number">3</span>][<span class="hljs-number">0</span>] = <span class="hljs-number">9</span>    <span class="comment"># Output: (4, 2, 3, [9, 5])</span><br />
                        <span class="keywords">print</span>(my_tuple)<br />
                        <br />
<span class="comment"># Tuples can be reassigned</span><br />
my_tuple = (<span class="hljs-string">'p'</span>, <span class="hljs-string">'r'</span>, <span class="hljs-string">'o'</span>, <span class="hljs-string">'g'</span>, <span class="hljs-string">'r'</span>, <span class="hljs-string">'a'</span>, <span class="hljs-string">'m'</span>, <span class="hljs-string">'i'</span>, <span class="hljs-string">'z'</span>)<br />
<br />
<span class="comment"># Output: ('p', 'r', 'o', 'g', 'r', 'a', 'm', 'i', 'z')</span><br />
                        <span class="keywords">print</span>(my_tuple)</code></pre> </div>
                        <div className='output'> 
                    <p><strong>Output</strong></p>

                    <pre><samp>(4, 2, 3, [9, 5])<br />
('p', 'r', 'o', 'g', 'r', 'a', 'm', 'i', 'z')</samp></pre></div>

                    <p>We can use <code>+</code> operator to combine two tuples. This is called <strong>concatenation</strong>.</p>

                    <p>We can also <strong>repeat</strong> the elements in a tuple for a given number of times using the <code>*</code> operator.</p>

                    <p>Both <code>+</code> and <code>*</code> operations result in a new tuple.</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Concatenation</span><br />
                        <span class="comment"># Output: (1, 2, 3, 4, 5, 6)</span><br />
                        <span class="keywords">print</span>((<span class="hljs-number">1</span>, <span class="hljs-number">2</span>, <span class="hljs-number">3</span>) + (<span class="hljs-number">4</span>, <span class="hljs-number">5</span>, <span class="hljs-number">6</span>))<br />
                        <br />
<span class="comment"># Repeat</span><br />
                        <span class="comment"># Output: ('Repeat', 'Repeat', 'Repeat')</span><br />
                        <span class="keywords">print</span>((<span class="hljs-string">"Repeat"</span>,) * <span class="hljs-number">3</span>)</code></pre> </div>
                        <div className='output'>
                    <p><strong>Output</strong></p>

                    <pre><samp>(1, 2, 3, 4, 5, 6)<br />
('Repeat', 'Repeat', 'Repeat')</samp></pre></div>

                    <hr />
                    <h2 id="delete">Deleting a Tuple</h2>

                    <p>As discussed above, we cannot change the elements in a tuple. It means that we cannot delete or remove items from a tuple.</p>

                    <p>Deleting a tuple entirely, however, is possible using the keyword <a href="/python-programming/keyword-list#del">del</a>.</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Deleting tuples</span><br />
my_tuple = (<span class="hljs-string">'p'</span>, <span class="hljs-string">'r'</span>, <span class="hljs-string">'o'</span>, <span class="hljs-string">'g'</span>, <span class="hljs-string">'r'</span>, <span class="hljs-string">'a'</span>, <span class="hljs-string">'m'</span>, <span class="hljs-string">'i'</span>, <span class="hljs-string">'z'</span>)<br />
<br />
<span class="comment"># can't delete items</span><br />
                        <span class="comment"># TypeError: 'tuple' object doesn't support item deletion</span><br />
                        <span class="comment"># del my_tuple[3]</span><br /><br />

                        <span class="comment"># Can delete an entire tuple</span><br />
                        <span class="keywords">del</span> my_tuple<br /><br />

<span class="comment"># NameError: name 'my_tuple' is not defined</span><br />
                        <span class="keywords">print</span>(my_tuple)</code></pre> </div>
                        <div className='output'> 
                    <p><strong>Output</strong></p>

                    <pre><samp>Traceback (most recent call last):<br />
                    File "&lt;string&gt;", line 12, in &lt;module&gt;<br />
NameError: name 'my_tuple' is not defined</samp></pre></div>

                    <hr />
                    <h2 id="method">Tuple Methods</h2>

                    <p>Methods that add items or remove items are not available with tuple. Only the following two methods are available.</p>

                    <p>Some examples of Python tuple methods:</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs">my_tuple = (<span class="hljs-string">'a'</span>, <span class="hljs-string">'p'</span>, <span class="hljs-string">'p'</span>, <span class="hljs-string">'l'</span>, <span class="hljs-string">'e'</span>,)<br />
<br />
<span class="keywords">print</span>(my_tuple.count(<span class="hljs-string">'p'</span>))  <span class="comment"># Output: 2</span> <br />
                        <span class="keywords">print</span>(my_tuple.index(<span class="hljs-string">'l'</span>))  <span class="comment"># Output: 3</span></code></pre> <br /> </div>
                        <div className='output'>
                    <p><strong>Output</strong></p>

                    <pre><samp>2<br />
3</samp></pre></div>

                    <hr />
                    <h2 id="other">Other Tuple Operations</h2>

                    <h3 id="member">1. Tuple Membership Test</h3>

                    <p>We can test if an item exists in a tuple or not, using the keyword <code>in</code>.</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Membership test in tuple</span> <br />
my_tuple = (<span class="hljs-string">'a'</span>, <span class="hljs-string">'p'</span>, <span class="hljs-string">'p'</span>, <span class="hljs-string">'l'</span>, <span class="hljs-string">'e'</span>,) <br />
<br /> <br />

<span class="comment"># In operation</span> <br />
                        <span class="keywords">print</span>(<span class="hljs-string">'a'</span> <span class="keywords">in</span> my_tuple)<br />
<span class="keywords">print</span>(<span class="hljs-string">'b'</span> <span class="keywords">in</span> my_tuple)<br />
<br />
<span class="comment"># Not in operation</span><br />
                        <span class="keywords">print</span>(<span class="hljs-string">'g'</span> <span class="keywords">not</span> <span class="keywords">in</span> my_tuple)</code></pre><br /> </div>
<div className='output'>
                    <p><strong>Output</strong></p><br />
                    <br />
                    <pre><samp>True<br />
                    False<br />
True</samp></pre></div>

                    <hr />
                    <h3 id="iterate">2. Iterating Through a Tuple</h3>

                    <p>We can use a <code>for</code> loop to iterate through each item in a tuple.</p>
                    <div className='containers'>
                    <pre class="python-exec" style={{ maxHeight: '600px' }}><code class="python hljs"><span class="comment"># Using a for loop to iterate through a tuple</span><br />
                        <span class="keywords">for</span> name <span class="keywords">in</span> (<span class="hljs-string">'John'</span>, <span class="hljs-string">'Kate'</span>):<br />
    <span class="keywords">print</span>(<span class="hljs-string">"Hello"</span>, name)</code></pre><br />
</div>
<div className='output'>
                    <p><strong>Output</strong></p>

                    <pre><samp>Hello John<br />
Hello Kate</samp></pre></div>

                    <hr />
                    <h3 id="advantages">Advantages of Tuple over List</h3>

                    <p>Since tuples are quite similar to lists, both of them are used in similar situations. However, there are certain advantages of implementing a tuple over a list. Below listed are some of the main advantages:</p>

                    <ul>
                        <li>We generally use tuples for heterogeneous (different) data types and lists for homogeneous (similar) data types.</li>
                        <li>Since tuples are immutable, iterating through a tuple is faster than with list. So there is a slight performance boost.</li>
                        <li>Tuples that contain immutable elements can be used as a key for a dictionary. With lists, this is not possible.</li>
                        <li>If you have data that doesn't change, implementing it as tuple will guarantee that it remains write-protected.</li>
                    </ul>
                </div>



            </div>

        </div>


    )
}

export default Tuple
